// shopify
window.Shopify = window.Shopify || {};
Shopify.fetchConfig = (type = 'json') => ({
  method: 'POST',
  headers: {
    'Content-Type': `application/${type}`,
    Accept: `application/${type}`,
    'X-Requested-With': 'XMLHttpRequest'
  },
  credentials: 'same-origin'
});

// gtag
window.dataLayer = window.dataLayer || [];
const originalGtag = window.gtag || function(){ window.dataLayer.push(arguments); };
window.gtag = function() {
  const args = Array.from(arguments);
  if (args[0] === 'consent') {
    const payload = args[2] || {};
    if (payload._source === 'privacy-banner') {
      delete payload._source;
      return originalGtag.apply(this, args);
    }
    return;
  }
  return originalGtag.apply(this, args);
};
gtag('consent', 'default', {
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  ad_storage: 'denied',
  analytics_storage: 'denied',
  functionality_storage: 'granted',
  personalization_storage: 'denied',
  security_storage: 'granted',
  _source: 'privacy-banner'
});

gtag('js', new Date());
gtag('config', 'G-WW2BVZDZTS', { send_page_view: false });

// privacy-banner
class PrivacyBanner extends HTMLElement {
  constructor() {
    super();
    this._onConsentLoaded = this._onConsentLoaded.bind(this);
    this._onClick = this._onClick.bind(this);
    window.Shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], this._onConsentLoaded);
  }

  connectedCallback() {
    this.banner = this.querySelector('#privacy-banner');
    this.popup = this.querySelector('#privacy-details');
    this.addEventListener('click', this._onClick);
    this._bannerObserver = new MutationObserver(() => {
      if (this.banner.open && this.banner.offsetHeight === 0) {
        this.banner.close();
        this._applyTagGating({ analyticsAllowed: false, marketingAllowed: false, preferencesAllowed: false });
      }
    });
    this._bannerObserver.observe(this.banner, { attributes: true, attributeFilter: ['style', 'class', 'open'] })
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
    this._bannerObserver?.disconnect();
  }

  _onConsentLoaded = (err) => {
    if (err) return;
    const cp = window.Shopify.customerPrivacy;
    const state = { analyticsAllowed: cp.analyticsProcessingAllowed(), marketingAllowed: cp.marketingAllowed(), preferencesAllowed: cp.preferencesProcessingAllowed()};
    document.dispatchEvent(new CustomEvent('customerPrivacyReady', { detail: state }));
    const hasChoice = cp.getTrackingConsent() !== 'no_interaction';
    if (!this.hasAttribute('button-control') && !hasChoice) this.banner.show();
    if (hasChoice) {
      this._applyTagGating(state);
    }
    document.addEventListener('visitorConsentCollected', (e) => {
      this._applyTagGating(e.detail);
    });
  };

  _onClick(e) {
    const a = e.target?.dataset?.action;
    if (a === 'accept') this._acceptAll();
    if (a === 'decline') this._declineAll();
    if (a === 'save') this._saveGranular();
    if (e.target?.dataset?.dialog === 'privacy-details') {
      this.querySelector('#analytics').checked = window.Shopify.customerPrivacy.analyticsProcessingAllowed();
      this.querySelector('#marketing').checked = window.Shopify.customerPrivacy.marketingAllowed();
      this.querySelector('#preferences').checked = window.Shopify.customerPrivacy.preferencesProcessingAllowed();
    }
  }

  _acceptAll() {
    window.Shopify.customerPrivacy.setTrackingConsent(
      { analytics: true, marketing: true, preferences: true },
      () => (this.banner.close(), this.popup.close())
    );
  }

  _declineAll() {
    window.Shopify.customerPrivacy.setTrackingConsent(
      { analytics: false, marketing: false, preferences: false },
      () => (this.banner.close(), this.popup.close())
    );
  }

  _saveGranular() {
    const analytics = this.querySelector('#analytics')?.checked ?? false;
    const marketing = this.querySelector('#marketing')?.checked ?? false;
    const preferences = this.querySelector('#preferences')?.checked ?? false;
    try {
      window.Shopify.customerPrivacy.setTrackingConsent(
        { analytics, marketing, preferences },
        () => (this.banner.close(), this.popup.close())
      );
    } catch {
      alert(window.privacyBannerStrings?.blocked);
    }
  }

  _applyTagGating(detail) {
    const a = detail?.analyticsAllowed ?? window.Shopify.customerPrivacy.analyticsProcessingAllowed();
    const m = detail?.marketingAllowed ?? window.Shopify.customerPrivacy.marketingAllowed();
    const p = detail?.preferencesAllowed ?? window.Shopify.customerPrivacy.preferencesProcessingAllowed();
    window.gtag?.('consent', 'update', {
      analytics_storage: a ? 'granted' : 'denied',
      ad_storage: m ? 'granted' : 'denied',
      ad_user_data: m ? 'granted' : 'denied',
      ad_personalization:m ? 'granted' : 'denied',
      functionality_storage: 'granted',
      personalization_storage: p ? 'granted' : 'denied',
      security_storage: 'granted',
      _source: 'privacy-banner'
    });
    document.documentElement.toggleAttribute('consent-analytics', a);
    document.documentElement.toggleAttribute('consent-marketing', m);
    document.documentElement.toggleAttribute('consent-preferences', p);
  }
}

window.customElements.define("privacy-banner", PrivacyBanner);

// announcement-bar
class AnnouncementBar extends HTMLElement {
  connectedCallback() {
    const bar = this.querySelector('.scroll-area');
    const slides = [...bar.children];
    if (slides.length < 2) return;
    let idx = 0;
    let offsets = slides.map(s => s.offsetLeft);
    let timer;
    let scrollIdle;
    window.addEventListener('resize', () => offsets = slides.map(slide => slide.offsetLeft), { passive: true });
    const play = () =>
      timer = setTimeout(() => {
        bar.scrollTo({ left: offsets[++idx % offsets.length], behavior: 'smooth' });
        play();
      }, 8000);
    bar.addEventListener('mouseenter', () => clearTimeout(timer));
    bar.addEventListener('scroll', () => {
      clearTimeout(timer);
      clearTimeout(scrollIdle);
      idx = (offsets.findIndex(off => off >= bar.scrollLeft) + offsets.length) % offsets.length;
      scrollIdle = setTimeout(play, 800);
    });
    bar.addEventListener('mouseleave', () => play());
    play();
  }
}
  
customElements.define('announcement-bar', AnnouncementBar);
  
// dialogs
(() => {
  function openDialog(trigger, e) {
    const id = trigger.dataset.dialog;
    if (!id) return;
    const dlg = document.getElementById(id);
    if (!dlg) return;
    e?.preventDefault?.();
    try {
      if (typeof dlg.showModal === 'function') {
        dlg.showModal();
      } else if (typeof dlg.show === 'function') {
        dlg.show();
      }
    } catch (_) {}
  }

  function closeDialog(trigger, e) {
    const dlg = trigger.closest('dialog');
    if (!dlg) return;
    e?.preventDefault?.();
    try { dlg.close(); } catch (_) {}
  }

  document.addEventListener('click', (e) => {
    const closeTrigger = e.target.closest('[data-dialog-close]');
    if (closeTrigger) {
      closeDialog(closeTrigger, e);
    }
    const openTrigger  = e.target.closest('[data-dialog]');
    if (openTrigger) {
      openDialog(openTrigger, e);
      return;
    }
  });
})();

// localization
class LocalizationForm extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const form = this.querySelector('form');
    if (!form) return;
    form.addEventListener('change', e => {
      if (e.target.matches('input[name="country_code"]')) {
        form.submit();
      }
    });
  }
}

customElements.define('localization-form', LocalizationForm);

// product-info
class ProductInfo extends HTMLElement {
  connectedCallback () {
    if (this._hydrated) return;
    this._hydrated = true;
    this._parseVariantMap();
    this._blocks = this._collectBlocks();
    this._abort = null;
    this._onVariantChange = this._onVariantChange.bind(this);
    this.addEventListener('variant:change', this._onVariantChange);
  }

  _parseVariantMap () {
    const script = this.querySelector('script[data-variants]');
    try {
      this.variantMap = script ? JSON.parse(script.textContent.trim()) : {};
    } catch (err) {
      console.warn('[product-info] Variant JSON parse error', err);
      this.variantMap = {};
    }
  }

  _collectBlocks () {
    return Array.from(this.closest('product').querySelectorAll('[data-block]'))
      .reduce((map, el) => {
        const key = el.dataset.block;
        if (key) map[key] = el;
        return map;
      }, {});
  }

  async _onVariantChange (evt) {
    const variantId = evt.detail.variantId;
    if (!variantId || this._refreshing) return;
    if (this._abort) this._abort.abort();
    this._abort = new AbortController();
    this.toggleAttribute('loading', true)
    try {
      const html = await fetch(
        `${window.location.pathname}?variant=${encodeURIComponent(variantId)}&view=blocks`,
        { headers: { 'Accept': 'text/html' }, signal: this._abort.signal }
      ).then(r => r.ok ? r.text() : '');
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newBlocks = Array.from(doc.querySelectorAll('[data-block]'))
        .reduce((m, el) => { m[el.dataset.block] = el; return m; }, {});
      for (const name in newBlocks) {
        const current = this._blocks[name];
        const fresh = newBlocks[name];
        if (current && fresh && !current.isEqualNode(fresh)) current.replaceWith(fresh);
      }
      this._blocks = this._collectBlocks();
      const newURL = new URL(window.location.href);
      newURL.searchParams.set('variant', variantId);
      history.replaceState({}, '', newURL);
    } catch (err) {
      if (err?.name !== 'AbortError') {
        console.error('[product-info] Blocks refresh failed', err);
      }
    } finally {
      if (this._abort?.signal?.aborted) return;
      this.toggleAttribute('loading', false);
    }
  }
}

customElements.define('product-info', ProductInfo);

// product-form
class ProductForm extends HTMLElement {
  connectedCallback () {
    if (this._hydrated) return;
    this._hydrated = true;
    this.form = this.querySelector('form');
    if (!this.form) return;
    const info = this.closest('product-info');
    this.variantMap = info?.variantMap || {};
    this.idInput = this.form.querySelector('input[name="id"]');
    this.submitBtn = this.form.querySelector('[type="submit"]');
    this.form.addEventListener('change', this._onChange.bind(this));
    this.form.addEventListener('submit', this._onSubmit.bind(this));
    this._onChange({ silent: true });
  }

  _onChange (opts = {}) {
    const values = [...this.form.querySelectorAll('input[type=radio]:checked')].map(el => el.value);
    const key = values.join('/');
    const variantId = this.variantMap[key];
    if (opts.silent) return;
    if (variantId) this.idInput.value = variantId;
    this.dispatchEvent(new CustomEvent('variant:change', {
      bubbles: true,
      detail: { variantId }
    }));
  }

  async _onSubmit (e) {
    e.preventDefault();
    if (this.submitBtn.disabled) return;

    if (!this.idInput.value) {
      this.submitBtn.textContent = this.submitBtn.dataset.selectMsg;
      return;
    }

    this._updateButton(true);

    try {
      const res = await fetch(`${routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: new FormData(this.form)
      });
      if (!res.ok) throw new Error('Add to cart failed');
      setTimeout(() => this._updateButton(false), 750);
      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch (err) {
      console.error(err);
      this._updateButton(false);
    } finally {
      setTimeout(() => this._updateButton(false), 1500);
    }
  }

  _updateButton (state) {
    this.submitBtn.disabled = state;
    this.submitBtn.classList.toggle('loading', state);
  }
}

customElements.define('product-form', ProductForm);

// cart
class Cart extends HTMLElement {
  connectedCallback() {
    this.timers = {};
    this.cartDrawer = document.getElementById('cart-drawer');
    this.addEventListener('quantity:change', e => {
      const line = Number(e.target.closest('[data-line]')?.dataset.line);
      const qty = e.detail.value;
      if (!line || isNaN(qty)) return;
      clearTimeout(this.timers[line]);
      this.timers[line] = setTimeout(() => this._update(line, qty), 300);
    });
    document.addEventListener('cart:updated', () => {
      this._update().then(() => {
        setTimeout(() => this.cartDrawer?.showModal(), 500);
      });
    });
  }

  async _update(line, quantity) {
    const sectionId = this.dataset.sectionId;
    let item;
    if (line != null && quantity != null) {
      item = this.querySelector(`.cart-item[data-line="${line}"]`);
      if (item) item.toggleAttribute('loading', true)
    }
    try {
      let html;
      if (line != null && quantity != null) {
        const res = await fetch(
          `${routes.cart_change_url}.js?sections[]=${sectionId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ line, quantity })
          }
        );
        if (!res.ok) throw new Error('Cart update failed');
        const json = await res.json();
        html = json.sections[sectionId] || '';
      } else {
        const res = await fetch(`/?sections[]=${sectionId}`, {
          headers: { 'Accept': 'application/json' }
        });
        if (!res.ok) throw new Error('Cart refresh failed');
        const json = await res.json();
        html = json[sectionId] || '';
      }
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newCart = doc.querySelector('cart-main');
      if (newCart) this.replaceWith(newCart);
      const newCount = doc.getElementById('cart-drawer-item-count');
      if (newCount) { document.getElementById('cart-drawer-item-count')?.replaceWith(newCount); }
      document.dispatchEvent(new CustomEvent('cart:changed', { detail: { reason: 'line-change' } }));
    } catch (err) {
      console.error(err);
      if (item) item.toggleAttribute('loading', false)
    }
  }
}

customElements.define('cart-main', Cart);

// quantity-input
class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('.quantity-input');
    this.decrement = this.querySelector('.decrease');
    this.increment = this.querySelector('.increase');
    this.min = +this.input.min || 0;
    this.max = +this.input.max || Infinity;
    this.prev = this.input.value;
  }

  connectedCallback() {
    this.decrement.addEventListener('click', () => this._update(+this.input.value + -1));
    this.increment.addEventListener('click', () => this._update(+this.input.value + 1));
    this.input.addEventListener('change', () => this._update(+this.input.value));

    this.input.addEventListener('keydown', e => {
      if (!e.key.match(/^\d$/) && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'].includes(e.key) && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
      }
    });
  }

  _update(val) {
    if (isNaN(val) || val < this.min || val > this.max) return this._revert();
    this.input.value = val;
    this.prev = val;
    this.dispatchEvent(new CustomEvent('quantity:change', {
      detail: { value: val },
      bubbles: true,
      composed: true
    }));
  }

  _revert() {
    this.input.value = this.prev;
    this.input.focus();
    this.input.reportValidity();
  }
}

customElements.define('quantity-input', QuantityInput);

// cart-discount
class CartDiscount extends HTMLElement {
  #activeFetch = null;

  connectedCallback() {
    this.sectionId = this.dataset.sectionId || 'cart-drawer';
    this.form = this.querySelector('form');
    this.input = this.querySelector('input[name="discount"]');
    this.submit = this.querySelector('[type="submit"], button[type="button"]:not([data-discount-code])');
    this.msg = this.querySelector('[data-discount-message]');
    if (!this.input || !this.msg) return;
    this.onSubmit = this.onSubmit.bind(this);
    this.onClick = this.onClick.bind(this);
    if (this.form) {
      this.form.addEventListener('submit', this.onSubmit);
    } else {
      this.submit?.addEventListener('click', this.onSubmit);
      this.input.addEventListener('keydown', (e) => { if (e.key === 'Enter') this.onSubmit(e); });
    }
    this.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.onSubmit);
    this.submit?.removeEventListener('click', this.onSubmit);
    this.removeEventListener('click', this.onClick);
    this.#activeFetch?.abort();
  }

  #abort() {
    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();
    return this.#activeFetch;
  }

  #busy(on) {
    this.submit?.classList.toggle('loading', on);
    this.submit?.toggleAttribute('disabled', on);
    this.input?.toggleAttribute('disabled', on);
    for (const el of this.querySelectorAll('[data-discount-code]')) {
      if (el instanceof HTMLButtonElement) el.disabled = on;
    }
  }

  #error(show) {
    this.msg.classList.toggle('hidden', !show);
  }

  #codes() {
    const out = [];
    for (const el of this.querySelectorAll('[data-discount-code]')) {
      const code = el instanceof HTMLElement ? el.dataset.discountCode : null;
      if (code) out.push(code);
    }
    return out;
  }

  #morph(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    doc.querySelector('cart-main') && document.querySelector('cart-main')?.replaceWith(doc.querySelector('cart-main'));
    const c = doc.getElementById('cart-drawer-item-count');
    c && document.getElementById('cart-drawer-item-count')?.replaceWith(c);
  }

  async #update(codes, { checkCode } = {}) {
    this.#error(false);
    this.#busy(true);
    const ac = this.#abort();
    try {
      const res = await fetch(`${routes.cart_update_url}.js?sections[]=${encodeURIComponent(this.sectionId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ discount: codes.join(',') }),
        signal: ac.signal
      });
      if (!res.ok) throw new Error('Discount update failed');
      const data = await res.json();
      if (checkCode && Array.isArray(data.discount_codes) && data.discount_codes.some(d => d?.code === checkCode && d?.applicable === false)) {
        return { data, invalid: true };
      }
      const html = data.sections?.[this.sectionId];
      if (!html) throw new Error('No section HTML returned');
      this.#morph(html);
      document.dispatchEvent(new CustomEvent('cart:changed', { detail: { reason: 'discount', cart: data } }));
      return data;
    } finally {
      this.#activeFetch = null;
      this.#busy(false);
    }
  }

  async onSubmit(e) {
    e.preventDefault();
    e.stopPropagation();
    const code = this.input.value.trim();
    if (!code) return this.input.focus();
    const existing = this.#codes();
    if (existing.includes(code)) return;
    try {
      const result = await this.#update([...existing, code], { checkCode: code });
      if (result?.invalid) {
        this.input.value = '';
        this.#error(true);
      }
    } catch (err) {
      if (err?.name !== 'AbortError') {
        console.error(err);
        this.#error(true);
      }
    }
  }

  async onClick(e) {
    const btn = e.target instanceof Element ? e.target.closest('[data-discount-code]') : null;
    if (!(btn instanceof HTMLButtonElement)) return;
    e.preventDefault();
    e.stopPropagation();
    const code = btn.dataset.discountCode;
    if (!code) return;
    const next = this.#codes().filter(c => c !== code);
    try {
      await this.#update(next);
    } catch (err) {
      if (err?.name !== 'AbortError') {
        console.error(err);
        this.#error(true);
      }
    }
  }
}

customElements.define('cart-discount', CartDiscount);

// add-cart-item
class AddCartItem extends HTMLElement {
  connectedCallback() {
    if (this._hydrated) return;
    this._hydrated = true;
    this.btn = this.querySelector('button');
    if (!this.btn) return;
    this.btn.addEventListener('click', (e) => this._onClick(e));
  }

  get variantId() { return this.dataset.id; }
  get quantity() {
    const q = Number(this.dataset.qty || 1);
    return Number.isFinite(q) && q > 0 ? q : 1;
  }

  _setLoading(state) {
    this.toggleAttribute('loading', state);
    this.btn.toggleAttribute('disabled', state);
    this.btn.classList.toggle('loading', state);
  }

  async _onClick(e) {
    e.preventDefault();
    e.stopPropagation();
    const id = this.variantId;
    if (!id || this.btn.disabled) return;
    this._setLoading(true);
    try {
      const fd = new FormData();
      fd.append('id', id);
      fd.append('quantity', String(this.quantity));
      const res = await fetch(`${routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: fd
      });
      if (!res.ok) throw new Error('Add to cart failed');
      document.dispatchEvent(new CustomEvent('cart:updated'));
      window.gtag('event', 'upsell', { source: 'cart_drawer', type: 'related_product' });
    } catch (err) {
      console.error(err);
    } finally {
      setTimeout(() => this._setLoading(false), 750);
    }
  }
}

customElements.define('add-cart-item', AddCartItem);

// product-gallery
class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this._setLightboxIndex, true);
  }

  _setLightboxIndex = (e) => {
    const media = e.target.closest('[data-dialog]');
    if (!media) return;
    const dlg = document.getElementById(media.dataset.dialog);
    const slider = dlg?.querySelector('slider-component');
    if (!slider) return;
    const items = [...this.querySelectorAll(`[data-dialog="${media.dataset.dialog}"]`)];
    const i = items.indexOf(media);
    if (i < 0) return;
    requestAnimationFrame(() => {
      slider.updateMetrics?.();
      const track = slider.track || slider.querySelector('.scroll-area');
      const slide = track?.children[i];
      if (!slide) return;
      slide.scrollIntoView({behavior: 'auto', inline: 'center', block: 'nearest'});
    });
  };
}

customElements.define('product-gallery', ProductGallery);

// product-recommendations
class RecommendedProducts extends HTMLElement {
  connectedCallback() {
    if (this.dataset.loaded) return;
    this.load();
  }

  async load() {
    try {
      const { sectionId, productId, limit } = this.dataset;
      const url = `${routes.recommendations_url}?product_id=${productId}&section_id=${sectionId}&limit=${limit}`;
      const res = await fetch(url);
      const html = await res.text();
      const temp = document.createElement('div');
      temp.innerHTML = html;
      const replacement = temp.querySelector(`recommended-products[data-section-id="${sectionId}"]`);
      if (replacement) this.replaceWith(replacement);
    } catch (err) {
      console.error('RecommendedProducts load failed', err);
    }
  }
}

customElements.define('recommended-products', RecommendedProducts);

// product-reviews
class ProductReviews extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;
    const form = this.querySelector('#product-reviews');
    const drawer = this.querySelector('#reviews-drawer');
    const toast = this.querySelector('#review-toast');
    const okEl = this.querySelector('#review-success');
    const errEl = this.querySelector('#review-error');
    toast?.open && toast.close();
    const showToast = ok => {
      if (!toast || !okEl || !errEl) return;
      toast.classList.toggle('success', ok);
      toast.classList.toggle('error', !ok);
      okEl.classList.toggle('hidden', !ok);
      errEl.classList.toggle('hidden', ok);
      typeof toast.show === 'function' ? toast.show() : (toast.open = true);
    };

    if (drawer && /(?:\?|&)write_review(?:[=&]|$)/.test(location.search)) {
      requestAnimationFrame(() => drawer.showModal?.() || (drawer.open = true));
    }

    form.addEventListener('submit', async e => {
      e.preventDefault();
      if (!form.reportValidity()) return;
      const btn = form.querySelector('[type="submit"]');
      if (btn) btn.disabled = true;
      try {
        await fetch(form.action, {
          method: 'POST',
          mode: 'no-cors',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams(new FormData(form)).toString()
        });
        form.reset();
        drawer?.close?.();
        showToast(true);
      } catch {
        showToast(false);
      } finally {
        if (btn) btn.disabled = false;
      }
    });
  }
}

customElements.define('product-reviews', ProductReviews);

// search
class Search extends HTMLElement {
  constructor() {
    super();
    this.cache = new Map();
    this.abortController = null;
    this.minChars = 1;
  }

  connectedCallback() {
    this.form = this.querySelector('form');
    this.input = this.querySelector('input[type="search"]');
    this.reset = this.querySelector('button[type="reset"]');
    this.results = this.querySelector('[data-search-results]');
    this.input.addEventListener('input', e => {
      const term = e.target.value.trim();
      if (term.length >= this.minChars) {
        this.reset.classList.remove('hidden');
        this._fetchResults(term);
      } else {
        this.reset.classList.add('hidden');
        this._updateURL('');
        this.results.innerHTML = '';
      }
    });
    this.reset.addEventListener('click', e => {
      e.preventDefault();
      this.input.value = '';
      this.reset.classList.add('hidden');
      this._updateURL('');
      this.results.innerHTML = '';
      this.input.focus();
    });
  }

  _buildSearchUrl(term) {
    const params = new URLSearchParams(new FormData(this.form));
    params.set('q', term);
    params.set('section_id', 'search');
    return `${this.form.action}?${params.toString()}`;
  }

  _updateURL(term) {
    const fetchUrl = this._buildSearchUrl(term);
    const params = new URLSearchParams(fetchUrl.split('?')[1]);
    params.delete('section_id');
    const cleanUrl = term.length ? `${this.form.action}?${params.toString()}` : this.form.action;
    history.replaceState(null, '', cleanUrl);
  }

  async _fetchResults(term) {
    if (this.cache.has(term)) {
      return this._renderResults(this.cache.get(term), term);
    }
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();
    try {
      const url = this._buildSearchUrl(term);
      const res = await fetch(url, { signal: this.abortController.signal });
      if (!res.ok) throw new Error(res.status);
      const html = await res.text();
      this.cache.set(term, html);
      this._renderResults(html, term);
    } catch (err) {
      if (err.name !== 'AbortError') console.error(err);
    }
  }

  _renderResults(htmlString, term) {
    if (!this.results) return;
    const tmp = document.createElement('div');
    tmp.innerHTML = htmlString;
    const newBlock = tmp.querySelector('[data-search-results]');
    if (!newBlock) return;
    this.results.innerHTML = newBlock.innerHTML;
    this._updateURL(term);
  }
}

customElements.define('search-component', Search);

// predictive-search
class PredictiveSearch extends HTMLElement {
  static get observedAttributes() {
    return ['data-types', 'data-limit'];
  }

  constructor() {
    super();
    this._abortController = null;
    this._onInput = this._onInput.bind(this);
  }

  connectedCallback() {
    this.input = this.querySelector('input[name="q"], input[type="search"]');
    this.reset = this.querySelector('button[type="reset"]');
    this.results = this.querySelector('#predictive-search-results');
    this._parseAttributes();
    this.default = this.results.innerHTML;
    this.input.addEventListener('input', this._onInput);
    this.reset.addEventListener('click', e => {
      e.preventDefault();
      this.input.value = '';
      this.results.innerHTML = this.default;
      this.reset.classList.add('hidden');
    });
  }

  disconnectedCallback() {
    this.input.removeEventListener('input', this._onInput);
    if (this._abortController) this._abortController.abort();
  }

  attributeChangedCallback(name, oldVal, newVal) {
    if (oldVal === newVal) return;
    this._parseAttributes();
  }

  _parseAttributes() {
    this.types = this.getAttribute('data-types') || 'product,collection,query,article,page';
    this.limit = parseInt(this.getAttribute('data-limit') || '10', 10);
  }

  async _onInput(evt) {
    const q = evt.target.value.trim();
    if (q.length < 1) {
      this.results.innerHTML = this.default;
      this.reset.classList.add('hidden');
      return;
    }
    this.reset.classList.remove('hidden');
    if (this._abortController) this._abortController.abort();
    this._abortController = new AbortController();
    const params = new URLSearchParams({section_id: 'predictive-search', q: q.toLowerCase(), 'resources[type]': this.types, 'resources[limit]': String(this.limit), 'resources[limit_scope]': 'each'});
    try {
      const resp = await fetch(`${routes.predictive_search_url}?${params}`, { signal: this._abortController.signal });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const html = await resp.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const fragment = doc.querySelector('#predictive-search-results');
      if (fragment) {
        this.results.innerHTML = fragment.innerHTML;
      } else {
        this.results.innerHTML = '<p class="error padding-small radius">No results container found</p>';
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('PredictiveSearch error:', err);
        this.results.innerHTML = '<p class="error padding-small radius">Failed to load results</p>';
      }
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);

// filters
class CollectionFilters extends HTMLElement {
  constructor() {
    super();
    this.refresh = this.refresh.bind(this);
    this.closeDialog = this.closeDialog.bind(this);
  }

  connectedCallback() {
    this.form = this.querySelector('form');
    this.sectionId = this.getAttribute('section-id');
    this.sectionEl = document.getElementById(this.sectionId);
    if (!this.form || !this.sectionEl) return;

    this.form.addEventListener('change', e => {
      if (e.target.matches('.checkbox input') || e.target.matches('select')) this.refresh();
    });

    this.form.addEventListener('click', e => {
      const clear = e.target.closest('a[data-clear]');
      if (!clear) return;
      e.preventDefault();
      const url = new URL(clear.href, location.origin);
      this.refresh(new URLSearchParams(), url.pathname);
    });

    this.form.addEventListener('submit', e => {
      e.preventDefault();
      this.refresh();
      this.closeDialog();
    });

    this.sectionEl.addEventListener('click', e => {
      const a = e.target.closest('[data-block="active-filters"] a');
      if (!a) return;
      e.preventDefault();
      this.refresh(new URL(a.href, location.origin).searchParams, a.pathname);
    });
  }

  refresh(params = new URLSearchParams(new FormData(this.form)), basePath = this.form.action.split('?')[0]) {
    const keep = ['q'];
    const current = new URLSearchParams(location.search);
    keep.forEach(k => {
      if (current.has(k) && !params.has(k)) params.set(k, current.get(k));
    });
    const fetchParams = new URLSearchParams(params);
    fetchParams.set('section_id', this.sectionId);
    fetch(`${basePath}?${fetchParams}`, { credentials: 'same-origin' })
      .then(r => r.text())
      .then(html => {
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        tmp.querySelectorAll('[data-block]').forEach(newBlock => {
          const sel = `[data-block="${newBlock.dataset.block}"]`;
          this.sectionEl.querySelector(sel)?.replaceWith(newBlock);
        });
      });

    const visibleQS = params.toString();
    history.replaceState(null, '', basePath + (visibleQS ? `?${visibleQS}` : ''));
  }

  closeDialog() {
    const dlg = this.closest('dialog');
    if (dlg?.close) dlg.close();
  }
}

customElements.define('collection-filters', CollectionFilters);

// product-counter
class ProductCounter extends HTMLElement {
  static requestCache = new Map();

  connectedCallback() {
    const countUrl = this.dataset.url;
    if (!countUrl) return;
    const countPromise =
      this.constructor.requestCache.get(countUrl) ||
      this.constructor.requestCache
        .set(
          countUrl,
          fetch(countUrl, { credentials: "same-origin" })
            .then((response) => (response.ok ? response.text() : "1"))
            .then((text) => Number(text.trim()) || 0)
            .catch(() => 1)
        )
        .get(countUrl);
    countPromise.then((count) => {
      if (count === 0) this.remove();
    });
  }
}

customElements.define("product-counter", ProductCounter);

// country-selector
class CountrySelector extends HTMLElement{
  connectedCallback(){
    const countryId = this.dataset.country;
    const provinceId = this.dataset.province;
    const country = countryId ? document.getElementById(countryId) : this.querySelector('select[name="address[country]"]');
    const province = provinceId ? document.getElementById(provinceId) : this.querySelector('select[name="address[province]"]');
    if(!country || !province) return;
    const wrapper = province.closest('.field');
    const initProvince = province.getAttribute('value') || '';
    const parseProvinces = (opt)=>{
      try { return JSON.parse(opt.getAttribute('data-provinces') || '[]'); }
      catch(e){ return []; }
    };
    const fill = ()=>{
      const opt = country.options[country.selectedIndex];
      const list = opt ? parseProvinces(opt) : [];
      province.innerHTML = '';
      if (list.length){
        const frag = document.createDocumentFragment();
        for (let i=0;i<list.length;i++){
          const o = document.createElement('option');
          o.value = list[i][0];
          o.textContent = list[i][1];
          frag.appendChild(o);
        }
        province.appendChild(frag);
        if (wrapper) wrapper.classList.remove('hidden');
        if (initProvince){
          for (let i=0;i<province.options.length;i++){
            const o = province.options[i];
            if (o.value === initProvince || o.text === initProvince){ province.selectedIndex = i; break; }
          }
        }
      } else {
        if (wrapper) wrapper.classList.add('hidden');
      }
    };
    country.addEventListener('change', fill);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fill, {once:true});
    else fill();
  }
}

customElements.define('country-selector', CountrySelector);

// newsletter
(() => {
  if (location.pathname === '/challenge') return

  const KEYS = { views:'views', status:'status' };
  const MIN_VIEWS = 2, DELAY_MS = 100, COOLDOWN_MS = 0.5*24*60*60*1000;

  const getStatus = () => { try { return JSON.parse(localStorage.getItem(KEYS.status) || '{}'); } catch { return {}; } };
  const setStatus = (patch) => {
    const next = { ...getStatus(), ...patch };
    try { localStorage.setItem(KEYS.status, JSON.stringify(next)); } catch {}
    return next;
  };

  const dlg = document.getElementById('newsletter');
  if (!dlg) return;
  
  const views = (parseInt(localStorage.getItem(KEYS.views),10) || 0) + 1;
  localStorage.setItem(KEYS.views, String(views));

  let hasConsent = false;

  document.addEventListener('customerPrivacyReady', e => {
    hasConsent = window.Shopify.customerPrivacy.getTrackingConsent() !== 'no_interaction';
    if (e.detail?.marketingAllowed) {
      document.querySelectorAll('input[name="contact[tags]"], input[name="customer[tags]"]').forEach(el => {
        if (!el.value.includes('consent')) el.value += ',consent';
      });
    }
    getStatus().state === 'reopen' ? open() : setTimeout(open, DELAY_MS);
  });

  const TAG_KEY = 'tags';

  const addTags = (extraTags) => {
    document.querySelectorAll('input[name="contact[tags]"], input[name="customer[tags]"]').forEach(el => {
      const have = new Set(el.value.split(',').map(s => s.trim()).filter(Boolean));
      extraTags.forEach(t => have.add(t));
      have.add('unprocessed');
      el.value = Array.from(have).join(',');
    });
  };

  const sanitize = s => String(s || '').toLowerCase().replace(/[,\s]+/g, '-').replace(/[^a-z0-9:_\-]/g, '-').replace(/-+/g, '-');

  let stored = [];
  try { stored = JSON.parse(localStorage.getItem(TAG_KEY) || '[]'); } catch {}

  if (!stored.length) {
    let refTag = 'ref:direct';
    const ref = document.referrer || '';
    if (ref) {
      const refHost = new URL(ref).host;
      if (refHost && refHost !== location.host) refTag = `ref:${sanitize(refHost)}`;
    }

    const url = new URL(location.href);
    const keys = ['utm_source','utm_medium','utm_campaign','utm_term','utm_content','campaign','gclid','fbclid','msclkid'];
    const utmTags = [];
    keys.forEach(k => {
      const v = url.searchParams.get(k);
      if (!v) return;
      if (k.startsWith('utm_')) utmTags.push(`${k}:${sanitize(v)}`);
      else utmTags.push(`${k}:${sanitize(v)}`);
    });

    stored = [refTag, ...utmTags];
    try { localStorage.setItem(TAG_KEY, JSON.stringify(stored)); } catch {}
  }

  addTags(stored);

  if (document.querySelector('.newsletter-form [success]')) {
    setStatus({ subscribed: true });
  }

  dlg.querySelector('form')?.addEventListener('submit', () => {
    setStatus({ state: 'reopen' });
  });

  function canShow() {
    const status = getStatus();
    if (dlg.open) return false;
    if (!hasConsent) return false;
    if (status.state === 'reopen') return true;
    if (status.subscribed) return false;
    if (views < MIN_VIEWS) return false;
    if (status.state === 'closed') return false;
    if (status.state === 'cooldown' && Date.now() < (status.until || 0)) return false;
    return true;
  }

  function open() {
    if (canShow()) {
      dlg.showModal();
      if (getStatus().state === 'reopen') setStatus({ state: 'none' });
    }
  }

  dlg.addEventListener('close', () => {
    const status = getStatus();
    if (!status.subscribed) setStatus({ state:'cooldown', until: Date.now() + COOLDOWN_MS });
  });
})();

// slider
class Slider extends HTMLElement {
  connectedCallback() {
    if (this._hydrated) return;
    this._hydrated = true;
    const prev = this.querySelector('.prev');
    const next = this.querySelector('.next');
    const track = this.querySelector('.scroll-area');
    if (!prev || !next || !track) return;
    Object.assign(this, { prev, next, track });
    this.slides = [...track.children];
    this.index = 0;
    this._ticking = false;
    prev.addEventListener('click', () => this.goto(-1));
    next.addEventListener('click', () => this.goto(1));
    window.addEventListener('resize', () => this.updateMetrics());
    track.addEventListener('scroll', () => {
      if (this._ticking) return;
      this._ticking = true;
      requestAnimationFrame(() => {
        this._ticking = false;
        this._onScroll();
      });
    }, { passive: true });
    if (this.hasAttribute('allow-drag')) {
      let startX = 0, dragging = false;
      track.addEventListener('mousedown', e => {
        startX = e.clientX;
        dragging = true;
      });
      window.addEventListener('mouseup', e => {
        if (!dragging) return;
        dragging = false;
        const diff = e.clientX - startX;
        if (Math.abs(diff) > 30) this.goto(diff < 0 ? 1 : -1);
      });
    }
    this.track.querySelectorAll('img').forEach(img => {
      if (!img.complete) img.addEventListener('load', () => this.updateMetrics(), { once: true });
    });
    this.updateMetrics();
  }

  updateMetrics() {
    const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
    const slideRect = this.slides[0].getBoundingClientRect();
    this.slideWidth = slideRect.width + gap;
    this.maxIndex = this.slides.length - 1;
    this._onScroll();
  }

  goto(direction) {
    const delta = direction * this.slideWidth;
    this.track.scrollBy({ left: delta, behavior: 'smooth' });
  }

  _onScroll() {
    const trackRect = this.track.getBoundingClientRect();
    const centerX = trackRect.left + trackRect.width / 2;
    let closestIndex = 0;
    let closestDist = Infinity;
    this.slides.forEach((s, i) => {
      const r = s.getBoundingClientRect();
      const mid = r.left + r.width / 2;
      const dist = Math.abs(mid - centerX);
      if (dist < closestDist) {
        closestDist = dist;
        closestIndex = i;
      }
    });
    this.index = Math.min(Math.max(closestIndex, 0), this.maxIndex);
    this._updateControls();
  }

  _updateControls() {
    const trackRect = this.track.getBoundingClientRect();
    const firstRect = this.slides[0].getBoundingClientRect();
    const lastRect = this.slides[this.slides.length - 1].getBoundingClientRect();
    const epsilon = 1;
    const atStart = firstRect.left >= trackRect.left - epsilon;
    const atEnd = lastRect.right <= trackRect.right + epsilon;
    this.prev.disabled = atStart;
    this.next.disabled = atEnd;
    this.slides.forEach((s, i) => s.classList.toggle('active', i === this.index));
  }
}

customElements.define('slider-component', Slider);

// tab-panel
class TabPanel extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('.tab-toggle').forEach(toggle => {
      toggle.addEventListener('click', () => this.activate(toggle))
    })
  }

  activate(toggle) {
    this.querySelectorAll('.tab-toggle').forEach(t => t.classList.remove('active'))
    this.querySelectorAll('.tab-content').forEach(c => c.classList.add('hidden'))
    toggle.classList.add('active')
    this.querySelector(`#${toggle.dataset.tab}`).classList.remove('hidden')
  }
}

customElements.define('tab-panel', TabPanel)

// cart-bubble
async function syncCartBubble() {
  const el = document.querySelector('[data-cart-count]');
  const count = await (await fetch(`/?view=cart_icon`)).text();
  el.setAttribute('data-cart-count', count.trim());
}
document.addEventListener('cart:updated', syncCartBubble);
document.addEventListener('cart:changed', syncCartBubble);

// cart-attributes
(() => {
  const STORE = sessionStorage;
  const FIELDS = [ 'campaign', 'source', 'medium', 'term', 'content', 'referrer'];

  function getAttribution() {
    const url = new URL(location.href);
    const p = k => url.searchParams.get(k) || '';
    const fresh = {
      campaign: p('utm_campaign') || p('campaign') || p('gclid') || p('fbclid') || p('msclkid'),
      source: p('utm_source'),
      medium: p('utm_medium'),
      term: p('utm_term'),
      content: p('utm_content'),
    };
    for (const [k, v] of Object.entries(fresh)) {
      if (v) STORE.setItem(k, v);
    }
    const refUrl = document.referrer ? new URL(document.referrer) : null;
    if (refUrl && refUrl.host && refUrl.host !== location.host) {
      STORE.setItem('referrer', refUrl.host.toLowerCase());
    }
    return Object.fromEntries(FIELDS.map(k => [k, STORE.getItem(k) || '']));
  }

  async function updateCartAttributes() {
    const attribution = getAttribution();
    const attrs = {};
    for (const k of FIELDS) {
      const v = attribution[k];
      if (v && v !== STORE.getItem(`sent_${k}`)) attrs[k] = v;
    }
    if (!Object.keys(attrs).length) return;
    try {
      await fetch(`${routes.cart_update_url}.js`, {
        ...Shopify.fetchConfig('json'),
        body: JSON.stringify({ attributes: attrs })
      });
      for (const k of Object.keys(attrs)) {
        STORE.setItem(`sent_${k}`, attrs[k]);
      }
    } catch {}
  }

  document.addEventListener('DOMContentLoaded', updateCartAttributes);
  document.addEventListener('cart:updated', updateCartAttributes);
})();